import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface UIState {
  sidebarCollapsed: boolean;
  drawerOpen: boolean;
  isMobile: boolean;
}

interface UIApi {
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
}

type UIContextValue = UIState & UIApi;

const UIContext = createContext<UIContextValue | undefined>(undefined);

const STORAGE_KEY = "aequia:sidebar-mode";
const LEGACY_STORAGE_KEY = "aeq-ui-sidebar";
const MQ = "(max-width: 900px)";

function readSidebarCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  const mode = localStorage.getItem(STORAGE_KEY);
  if (mode === "icon-only") return true;
  if (mode === "expanded") return false;
  return localStorage.getItem(LEGACY_STORAGE_KEY) === "1";
}

export function UIProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    return readSidebarCollapsed();
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.matchMedia(MQ).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(MQ);
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const setSidebarCollapsed = useCallback((v: boolean) => {
    setSidebarCollapsedState(v);
    localStorage.setItem(STORAGE_KEY, v ? "icon-only" : "expanded");
    localStorage.setItem(LEGACY_STORAGE_KEY, v ? "1" : "0");
  }, []);

  const toggleSidebar = useCallback(
    () => setSidebarCollapsed(!sidebarCollapsed),
    [sidebarCollapsed, setSidebarCollapsed],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
        event.preventDefault();
        setSidebarCollapsedState((current) => {
          const next = !current;
          localStorage.setItem(STORAGE_KEY, next ? "icon-only" : "expanded");
          localStorage.setItem(LEGACY_STORAGE_KEY, next ? "1" : "0");
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const value = useMemo<UIContextValue>(
    () => ({
      sidebarCollapsed,
      drawerOpen,
      isMobile,
      toggleSidebar,
      setSidebarCollapsed,
      openDrawer,
      closeDrawer,
    }),
    [
      sidebarCollapsed,
      drawerOpen,
      isMobile,
      toggleSidebar,
      setSidebarCollapsed,
      openDrawer,
      closeDrawer,
    ],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI() {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error("useUI must be used within <UIProvider>");
  return ctx;
}
